<template>
  <div v-if="event" ref="pageRef" class="page-container">
    <span class="no-print" data-html2canvas-ignore="true">
      <button
        class="main-button v-btn v-btn--depressed v-btn--round theme--light primary"
        @click="openPrint"
      >
        Print
      </button>
      <button
        class="main-button v-btn v-btn--depressed v-btn--round theme--light primary"
        @click="downloadPdf"
      >
        Download
      </button>
    </span>
    <p class="date">Vrijeme generiranja izvještaja: {{ todayDate }} {{ currTime }}</p>
    <h1 class="page-title">Izvještaj eventa, {{ event.title }} {{ formatDateTime(event.from) }}</h1>
    <div>
      <p class="organization-data" style="font-weight: 800">
        {{ organization.legalName }}, OIB: {{ organization.OIB }}, {{ organization.address }},
        {{ organization.postalCode }} {{ organization.city }}
      </p>
    </div>
    <div class="divider" />
    <div class="table-info">
      <section>
        <p>Kapacitet dvorane</p>
        <p>{{ Math.max(statistics.venueCapacity, statistics.ticketsInSales) }}</p>
      </section>
      <section>
        <p>Ulaznice u prodaji</p>
        <p>{{ statistics.ticketsInSales }}</p>
      </section>
      <section>
        <p>Ulaznice onemogućene za prodaju</p>
        <p>{{ Math.max(statistics.venueCapacity - statistics.ticketsInSales, 0) }}</p>
      </section>
    </div>
    <div class="divider" />
    <p class="basic">Prodaja po stavkama</p>
    <div class="table-info">
      <section>
        <p>Ukupno gratis ulaznica</p>
        <p>
          {{ totalGratisCount }} ({{
            round((100 * totalGratisCount) / (totalGratisCount + totalSoldCount))
          }}%)
        </p>
      </section>
      <section>
        <p>Ukupno ulaznica kupljenih putem narudžbenice</p>
        <p>
          {{ totalOrderFormCount }} ({{
            round((100 * totalOrderFormCount) / (totalGratisCount + totalSoldCount))
          }}%)
        </p>
      </section>
      <section>
        <p>Online kupnja</p>
        <p>
          {{ totalOnlineSoldCount }} ({{
            round((100 * totalOnlineSoldCount) / (totalGratisCount + totalSoldCount))
          }}%)
        </p>
      </section>
      <section>
        <p>Fizička kupnja (blagajna)</p>
        <p>{{ totalTicketSellerSoldCount }} ({{ calculateShare(totalTicketSellerSoldCount) }}%)</p>
      </section>
      <section>
        <p>Fizička kupnja (CoreEvent prodajno mjesto)</p>
        <p>
          {{ totalCoreEventSalesPointSoldCount }} ({{
            calculateShare(totalCoreEventSalesPointSoldCount)
          }}%)
        </p>
      </section>
      <section>
        <p>Kupnja poklon karticama</p>
        <p>{{ totalGiftCardSoldCount }} ({{ calculateShare(totalGiftCardSoldCount) }}%)</p>
      </section>
      <section>
        <p style="font-weight: bold">Ukupno izdanih ulaznica</p>
        <p style="font-weight: bold">
          {{ totalSoldCount + totalGratisCount }} ({{
            round((100 * (totalSoldCount + totalGratisCount)) / statistics.ticketsInSales)
          }}% popunjenost)
        </p>
      </section>
    </div>
    <div
      v-if="![502].includes(Number(organization.id)) && orderFormOrders && orderFormOrders.length"
    >
      <p class="basic">Popis narudženica</p>
      <div class="table-info">
        <section v-for="order in orderFormOrders" :key="order.id">
          <p v-if="order.legalEntity">
            {{ order.legalEntity.name }}, OIB: {{ order.legalEntity.OIB }},
            {{ order.legalEntity.address }} {{ order.legalEntity.city }}
            {{ order.legalEntity.zipCode }}
          </p>
          <p v-if="!order.legalEntity">{{ 'Fizičko prodajno mjesto' }}</p>
          <p>{{ order.purchases.length }}</p>
        </section>
      </div>
    </div>
    <p v-if="refundOrders && refundOrders.length" class="basic">Popis storno narudžbi</p>
    <div class="table-info">
      <section v-for="order in refundOrders" :key="order.id">
        <p>
          {{
            order.buyer && order.buyer.fields && order.buyer.fields.firstName
              ? `${order.buyer.fields.firstName} ${order.buyer.fields.lastName}`
              : 'Fizička prodaja'
          }}
        </p>
        <p>{{ order.purchases.length }} kom, {{ calculateOrderPrice(order) }} EUR</p>
      </section>
      <section v-if="refundOrders && refundOrders.length" style="font-weight: bold">
        <p>Ukupno</p>
        <p>{{ totalRefundedCount }} kom, {{ round(totalRefundedAmount) }} EUR</p>
      </section>
    </div>

    <div class="divider" />
    <p class="basic">Prodaja ulaznica</p>
    <div>
      <div class="report-data report-title">
        <p>Ulaznica</p>
        <p>Cijena (EUR)</p>
        <p>Online/Fizičke</p>
        <p>Promo kod popust</p>
        <p>Ukupno Kom</p>
        <p>Ukupno EUR</p>
        <p>Narudžbenica</p>
        <p>Pretplata</p>
      </div>
      <div v-for="(report, i) in reportData" :key="i">
        <div class="report-row">
          <p class="report-data">{{ report.name }}</p>
          <p class="report-data">{{ report.price }}</p>
          <p class="report-data">{{ getSaleChannelLabel(report) }}</p>
          <p class="report-data">
            {{
              report.promo_code_discount_percentage
                ? `${report.promo_code_discount_percentage}%`
                : ''
            }}
            {{
              report.promo_code_discount_amount ? `${report.promo_code_discount_amount} EUR` : ''
            }}
            {{
              !report.promo_code_discount_amount && !report.promo_code_discount_amount ? '-' : ''
            }}
          </p>
          <p class="report-data">
            {{
              Number(report.online || 0) +
                Number(report.offline || 0) +
                Number(report.giftCardCount || 0)
            }}
          </p>
          <p class="report-data">{{ report.total }}</p>
          <p class="report-data">{{ report.isOrderForm ? 'Narudžbenica' : '' }}</p>
          <p class="report-data">{{ report.prepaidCoupon || '' }}</p>
        </div>
      </div>
    </div>
    <div>
      <div class="report-row">
        <p class="report-data" style="font-weight: 700">Fizičke - blagajna</p>
        <p class="report-data" style="font-weight: 700">{{ regularTicketSellerSoldCount }} kom</p>
        <p class="report-data" style="font-weight: 700">{{ regularTicketSellerSoldAmount }} EUR</p>
      </div>
      <div class="report-row">
        <p class="report-data" style="font-weight: 700">Fizičke - CoreEvent prodajno mjesto</p>
        <p class="report-data" style="font-weight: 700">
          {{ regularCoreEventSalesPointSoldCount }} kom
        </p>
        <p class="report-data" style="font-weight: 700">
          {{ regularCoreEventSalesPointSoldAmount }} EUR
        </p>
      </div>
      <div class="report-row">
        <p class="report-data" style="font-weight: 700">Online</p>
        <p class="report-data" style="font-weight: 700">{{ regularOnlineSoldCount }} kom</p>
        <p class="report-data" style="font-weight: 700">{{ regularOnlineSoldAmount }} EUR</p>
      </div>
      <div class="report-row">
        <p class="report-data" style="font-weight: 700">Kulturna iskaznica</p>
        <p class="report-data" style="font-weight: 700">{{ totalKulturnaIskaznicaCount }} kom</p>
        <p class="report-data" style="font-weight: 700">
          {{ totalKulturnaIskaznicaAmount }} EUR
        </p>
      </div>
      <div v-if="totalKulturnaIskaznicaCashCount" class="report-row">
        <p class="report-data" style="font-weight: 700">Kulturna iskaznica, gotovina</p>
        <p class="report-data" style="font-weight: 700">
          {{ totalKulturnaIskaznicaCashCount }} kom
        </p>
        <p class="report-data" style="font-weight: 700">
          {{ totalKulturnaIskaznicaCashAmount }} (KI:
          {{ totalKulturnaIskaznicaCashKiAmount }}, gotovina:
          {{ totalKulturnaIskaznicaCashRemainderAmount }}) EUR
        </p>
      </div>
      <div v-if="totalKulturnaIskaznicaCardCount" class="report-row">
        <p class="report-data" style="font-weight: 700">Kulturna iskaznica, kartica</p>
        <p class="report-data" style="font-weight: 700">
          {{ totalKulturnaIskaznicaCardCount }} kom
        </p>
        <p class="report-data" style="font-weight: 700">
          {{ totalKulturnaIskaznicaCardAmount }} (KI: {{ totalKulturnaIskaznicaCardKiAmount }},
          kartica: {{ totalKulturnaIskaznicaCardRemainderAmount }}) EUR
        </p>
      </div>
      <div v-if="totalKulturnaIskaznicaOnlineCount" class="report-row">
        <p class="report-data" style="font-weight: 700">Kulturna iskaznica, ONLINE</p>
        <p class="report-data" style="font-weight: 700">
          {{ totalKulturnaIskaznicaOnlineCount }} kom
        </p>
        <p class="report-data" style="font-weight: 700">
          {{ totalKulturnaIskaznicaOnlineAmount }} (KI:
          {{ totalKulturnaIskaznicaOnlineKiAmount }}, ONLINE:
          {{ totalKulturnaIskaznicaOnlineRemainderAmount }}) EUR
        </p>
      </div>
      <div class="report-row">
        <p class="report-data" style="font-weight: 700">Narudžbenice</p>
        <p class="report-data" style="font-weight: 700">{{ totalOrderFormCount }} kom</p>
        <p class="report-data" style="font-weight: 700">{{ totalOrderFormSoldAmount }} EUR</p>
      </div>
      <div class="report-row" style="border-bottom: 1px #333 solid">
        <p class="report-data" style="font-weight: 700">Poklon kartice</p>
        <p class="report-data" style="font-weight: 700">{{ totalGiftCardSoldCount }} kom</p>
        <p class="report-data" style="font-weight: 700">{{ totalGiftCardSoldAmount }} EUR</p>
      </div>
      <div class="report-row" style="border-bottom: 1px #333 solid">
        <p class="report-data" style="font-weight: 700">Ukupno</p>
        <p class="report-data" style="font-weight: 700">{{ totalSoldCount }} kom</p>
        <p class="report-data" style="font-weight: 700">{{ totalSoldAmount }} EUR</p>
      </div>
    </div>
    <div v-if="ordersWithDescription.length">
      <p class="basic">Narudžbe s napomenom</p>
      <div>
        <div class="report-data report-title" style="display: flex">
          <p style="flex: 2">Broj narudžbe</p>
          <p style="flex: 4">Napomena</p>
        </div>
        <div
          v-for="order in ordersWithDescription"
          :key="order.id"
          class="order-with-description report-row report-data"
        >
          <p style="flex: 2">{{ order.id }}</p>
          <p style="flex: 4; line-height: normal">{{ order.description }}</p>
        </div>
      </div>
    </div>
    <div>
      <div class="divider" style="margin-top: 24px" />
      <p class="basic" style="margin: 24px 0">
        Rekapitulacija fizičke prodaje prema načinu plaćanja
      </p>
      <div>
        <div class="report-row">
          <p class="header-data" style="font-weight: 700">Gotovinska plaćanja</p>
        </div>
      </div>
      <div>
        <div class="report-row">
          <p class="report-data">Gotovina</p>
          <p class="report-data">{{ offlineRecapitulationCash.count }} kom</p>
          <p class="report-data text-right">{{ offlineRecapitulationCash.total }} eur</p>
        </div>
      </div>
      <div v-if="offlineRecapitulationKulturnaIskaznicaCash.count">
        <div class="report-row">
          <p class="report-data">Kulturna iskaznica, gotovina</p>
          <p class="report-data">{{ offlineRecapitulationKulturnaIskaznicaCash.count }} kom</p>
          <p class="report-data text-right">
            {{ offlineRecapitulationKulturnaIskaznicaCash.total }} eur
          </p>
        </div>
      </div>
      <div class="report-row">
        <p class="report-data text-right" style="font-weight: 700">Ukupno</p>
        <p class="report-data" style="font-weight: 700">
          {{ offlineRecapitulationCashAll.count }} kom
        </p>
        <p class="report-data text-right" style="font-weight: 700">
          {{ offlineRecapitulationCashAll.total }} eur
        </p>
      </div>
      <div>
        <div class="report-row">
          <p class="header-data" style="font-weight: 700">Platne/kreditne kartice</p>
        </div>
      </div>
      <div
        v-for="(cardRecapitulationData, cardIndex) in offlineRecapitulationCards"
        :key="`${cardRecapitulationData.bankCardType || 'card'}-${cardIndex}`"
      >
        <div>
          <div class="report-row">
            <p class="report-data">{{ cardRecapitulationData.bankCardType }}</p>
            <p class="report-data">{{ cardRecapitulationData.count }} kom</p>
            <p class="report-data text-right">{{ cardRecapitulationData.total }} eur</p>
          </div>
        </div>
      </div>
      <div v-if="offlineRecapitulationKulturnaIskaznicaCard.count">
        <div class="report-row">
          <p class="report-data">Kulturna iskaznica, kartica</p>
          <p class="report-data">{{ offlineRecapitulationKulturnaIskaznicaCard.count }} kom</p>
          <p class="report-data text-right">
            {{ offlineRecapitulationKulturnaIskaznicaCard.total }} eur
          </p>
        </div>
      </div>
      <div class="report-row">
        <p class="report-data text-right" style="font-weight: 700">Ukupno</p>
        <p class="report-data" style="font-weight: 700">
          {{ offlineRecapitulationCardsAll.count }} kom
        </p>
        <p class="report-data text-right" style="font-weight: 700">
          {{ offlineRecapitulationCardsAll.total }} eur
        </p>
      </div>
      <div v-if="offlineRecapitulationKulturnaIskaznica.count">
        <div class="report-row">
          <p class="header-data" style="font-weight: 700">Kulturna iskaznica</p>
        </div>
      </div>
      <div v-if="offlineRecapitulationKulturnaIskaznica.count">
        <div class="report-row">
          <p class="report-data">Kulturna iskaznica</p>
          <p class="report-data">{{ offlineRecapitulationKulturnaIskaznica.count }} kom</p>
          <p class="report-data text-right">
            {{ offlineRecapitulationKulturnaIskaznica.total }} eur
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { round } from 'mathjs'
import dayjs from 'dayjs'
import { GET_THEATER_EVENT_REPORT } from '../../graphql/purchase.graphql'
import { GET_ORGANIZATION } from '@/graphql/organization.graphql'

export default {
  props: {
    eventId: String,
    organizationSlug: String,
  },
  data() {
    return {
      gratisOrders: [],
      organization: {},
      statistics: {},
      event: {},
      reportData: [],
      orderFormOrders: [],
      ordersWithDescription: [],
      refundOrders: [],
      totalRefundedCount: 0,
      totalRefundedAmount: 0,
      zkmCustomEvents: [2053, 2051, 2052, 2050],
      2053: [6529, 6530],
      2051: [6530],
      2052: [6528],
      2050: [6528, 6529],
      customExtraEvent: {},
      totalSoldAmount: 0,
      totalSoldCount: 0,
      totalOfflineSoldCount: 0,
      totalOfflineSoldAmount: 0,
      totalOrderFormSoldAmount: 0,
      totalOrderFormSoldCount: 0,
      totalOnlineSoldCount: 0,
      totalGiftCardSoldCount: 0,
      totalGiftCardSoldAmount: 0,
      totalOnlineSoldAmount: 0,
      totalTicketSellerSoldCount: 0,
      totalTicketSellerSoldAmount: 0,
      totalCoreEventSalesPointSoldCount: 0,
      totalCoreEventSalesPointSoldAmount: 0,
      totalKulturnaIskaznicaCount: 0,
      totalKulturnaIskaznicaAmount: 0,
      totalKulturnaIskaznicaTicketSellerCount: 0,
      totalKulturnaIskaznicaTicketSellerAmount: 0,
      totalKulturnaIskaznicaCoreEventSalesPointCount: 0,
      totalKulturnaIskaznicaCoreEventSalesPointAmount: 0,
      totalKulturnaIskaznicaFullOnlineCount: 0,
      totalKulturnaIskaznicaFullOnlineAmount: 0,
      totalKulturnaIskaznicaCashCount: 0,
      totalKulturnaIskaznicaCashAmount: 0,
      totalKulturnaIskaznicaCashKiAmount: 0,
      totalKulturnaIskaznicaCashRemainderAmount: 0,
      totalKulturnaIskaznicaCardCount: 0,
      totalKulturnaIskaznicaCardAmount: 0,
      totalKulturnaIskaznicaCardKiAmount: 0,
      totalKulturnaIskaznicaCardRemainderAmount: 0,
      totalKulturnaIskaznicaOnlineCount: 0,
      totalKulturnaIskaznicaOnlineAmount: 0,
      totalKulturnaIskaznicaOnlineKiAmount: 0,
      totalKulturnaIskaznicaOnlineRemainderAmount: 0,
      totalGratisCount: 0,
      totalOrderFormCount: 0,
      todayDate: dayjs().format('DD.MM.YYYY'),
      currTime: dayjs().format('HH:mm'),
      offlineRecapitulationCash: {
        count: 0,
        total: 0,
      },
      offlineRecapitulationCashAll: {
        count: 0,
        total: 0,
      },
      offlineRecapitulationCardsAll: {
        count: 0,
        total: 0,
      },
      offlineRecapitulationKulturnaIskaznica: {
        count: 0,
        total: 0,
      },
      offlineRecapitulationKulturnaIskaznicaCash: {
        count: 0,
        total: 0,
      },
      offlineRecapitulationKulturnaIskaznicaCard: {
        count: 0,
        total: 0,
      },
      offlineRecapitulationCards: [],
    }
  },
  computed: {
    regularOnlineSoldCount() {
      return Math.max(
        this.totalOnlineSoldCount -
          this.totalKulturnaIskaznicaFullOnlineCount -
          this.totalKulturnaIskaznicaOnlineCount,
        0,
      )
    },
    regularOnlineSoldAmount() {
      return this.round(
        Math.max(
          this.totalOnlineSoldAmount -
            this.totalKulturnaIskaznicaFullOnlineAmount -
            this.totalKulturnaIskaznicaOnlineAmount,
          0,
        ),
      )
    },
    regularTicketSellerSoldCount() {
      return Math.max(
        this.totalTicketSellerSoldCount -
          this.totalKulturnaIskaznicaTicketSellerCount -
          this.totalKulturnaIskaznicaCashCount -
          this.totalKulturnaIskaznicaCardCount,
        0,
      )
    },
    regularTicketSellerSoldAmount() {
      return this.round(
        Math.max(
          this.totalTicketSellerSoldAmount -
            this.totalKulturnaIskaznicaTicketSellerAmount -
            this.totalKulturnaIskaznicaCashAmount -
            this.totalKulturnaIskaznicaCardAmount,
          0,
        ),
      )
    },
    regularCoreEventSalesPointSoldCount() {
      return Math.max(
        this.totalCoreEventSalesPointSoldCount -
          this.totalKulturnaIskaznicaCoreEventSalesPointCount,
        0,
      )
    },
    regularCoreEventSalesPointSoldAmount() {
      return this.round(
        Math.max(
          this.totalCoreEventSalesPointSoldAmount -
            this.totalKulturnaIskaznicaCoreEventSalesPointAmount,
          0,
        ),
      )
    },
  },
  async mounted() {
    this.event = await this.getPurchasesByEvent(this.eventId)
    if (this.zkmCustomEvents.includes(Number(this.eventId))) {
      this.customExtraEvent = await this.getPurchasesByEvent('2247')
      const correctCustomOrders = (this.customExtraEvent.refundOrders || []).filter(o => {
        const correctPurchases = o.purchases.filter(p =>
          this[Number(this.event.event.id)].includes(p.orderItemId),
        )
        o.purchases = correctPurchases.filter(Boolean)

        return correctPurchases.filter(Boolean).length
      })
      this.event.refundOrders = [
        ...(this.event.refundOrders || []),
        ...(correctCustomOrders || []).map(o => {
          const price = Number(o.price.split(' ')[1])

          return { ...o, price: `EUR ${price / 2}` }
        }),
      ]
      const correctCustomReports = (this.customExtraEvent.report || [])
        .map(r => {
          return {
            ...r,
            total: `${Number(r.total) / 2}`,
            price: `${Number(r.price) / 2}`,
            prepaidCoupon: null,
          }
        })
        .filter(r => this[Number(this.event.event.id)].includes(Number(r.orderItemId)))
        .filter(Boolean)
      this.event.report = [...this.event.report, ...correctCustomReports]
    }
    this.modifyEventReportData(this.event)
  },
  methods: {
    async downloadPdf() {
      const target = this.$refs.pageRef || document.querySelector('.page-container')
      if (!target) {
        return
      }

      const html2canvas = (await import('html2canvas')).default
      const { jsPDF } = await import('jspdf')

      const canvas = await html2canvas(target, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: target.scrollWidth,
        windowHeight: target.scrollHeight,
      })

      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF('p', 'mm', 'a4')
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()

      const imgWidth = pageWidth
      const imgHeight = (canvas.height * imgWidth) / canvas.width

      let heightLeft = imgHeight
      let position = 0

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST')
      heightLeft -= pageHeight

      while (heightLeft > 0) {
        position = heightLeft - imgHeight
        pdf.addPage()
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST')
        heightLeft -= pageHeight
      }

      pdf.save(`Izvjestaj-eventa-${dayjs().format('DD.MM.YYYY')}.pdf`)
    },
    async openPrint() {
      setTimeout(() => {
        window.print()
      }, 1)
    },
    formatDate(date) {
      return moment(date).format('DD. MM. YYYY')
    },
    round(num) {
      return round(num, 2)
    },
    formatDateTime(date) {
      return moment(date).format('DD. MM. YYYY HH:mm')
    },
    getYear(date) {
      return moment(date).year()
    },
    calculateOrderPrice(order) {
      return order.purchases.reduce((acc, purchase) => {
        return acc + parseFloat(purchase.orderItem.price)
      }, 0)
    },
    async getPurchasesByEvent(eventId) {
      const { data, errors } = await this.$apollo.query({
        query: GET_THEATER_EVENT_REPORT,
        variables: { eventId },
      })
      if (errors) {
        return
      } else {
        return data.getTheaterEventReport
      }
    },
    async modifyEventReportData(eventReport) {
      if (!eventReport) {
        return
      }
      const reportRows = eventReport.report || []
      this.ordersWithDescription = eventReport.ordersWithDescription || []
      this.reportData = reportRows.filter(r => {
        return r.total !== '0.00'
      })
      this.orderFormOrders = eventReport.orderFormOrders || []
      this.refundOrders = eventReport.refundOrders || []
      this.gratisOrders = eventReport.gratisOrders || []
      this.totalGratisCount = this.gratisOrders.reduce((acc, order) => {
        return acc + order.purchases.length
      }, 0)
      this.totalRefundedCount = this.refundOrders.reduce((acc, order) => {
        return acc + order.purchases.length
      }, 0)
      this.totalRefundedAmount = this.refundOrders.reduce((acc, order) => {
        return acc + this.calculateOrderPrice(order)
      }, 0)
      this.totalOrderFormCount = this.orderFormOrders.reduce((acc, report) => {
        return acc + report.purchases.length
      }, 0)
      this.event = eventReport.event
      this.statistics = eventReport.statistics || {}
      this.totalSoldAmount = 0
      this.totalSoldCount = 0
      this.totalOfflineSoldAmount = 0
      this.totalOfflineSoldCount = 0
      this.totalOnlineSoldAmount = 0
      this.totalOnlineSoldCount = 0
      this.totalGiftCardSoldCount = 0
      this.totalGiftCardSoldAmount = 0
      this.totalTicketSellerSoldAmount = 0
      this.totalTicketSellerSoldCount = 0
      this.totalCoreEventSalesPointSoldAmount = 0
      this.totalCoreEventSalesPointSoldCount = 0
      this.totalKulturnaIskaznicaCount = 0
      this.totalKulturnaIskaznicaAmount = 0
      this.totalKulturnaIskaznicaTicketSellerCount = 0
      this.totalKulturnaIskaznicaTicketSellerAmount = 0
      this.totalKulturnaIskaznicaCoreEventSalesPointCount = 0
      this.totalKulturnaIskaznicaCoreEventSalesPointAmount = 0
      this.totalKulturnaIskaznicaFullOnlineCount = 0
      this.totalKulturnaIskaznicaFullOnlineAmount = 0
      this.totalKulturnaIskaznicaCashCount = 0
      this.totalKulturnaIskaznicaCashAmount = 0
      this.totalKulturnaIskaznicaCashKiAmount = 0
      this.totalKulturnaIskaznicaCashRemainderAmount = 0
      this.totalKulturnaIskaznicaCardCount = 0
      this.totalKulturnaIskaznicaCardAmount = 0
      this.totalKulturnaIskaznicaCardKiAmount = 0
      this.totalKulturnaIskaznicaCardRemainderAmount = 0
      this.totalKulturnaIskaznicaOnlineCount = 0
      this.totalKulturnaIskaznicaOnlineAmount = 0
      this.totalKulturnaIskaznicaOnlineKiAmount = 0
      this.totalKulturnaIskaznicaOnlineRemainderAmount = 0
      this.totalOrderFormSoldAmount = 0
      for (const report of this.reportData) {
        const totalAmount = Number(report.total || 0)
        const onlineCount = Number(report.online || 0)
        const offlineCount = Number(report.offline || 0)
        const onlineTotal = Number(report.onlineTotal || 0)
        const coreEventCount = Number(report.coreEventSalesPointCount || 0)
        const coreEventTotal = Number(report.coreEventSalesPointTotal || 0)
        const ticketSellerCount = Number(report.ticketSellerCount || 0)
        const ticketSellerTotal = Number(report.ticketSellerTotal || 0)
        const giftCardCount = Number(report.giftCardCount || 0)
        const giftCardTotal = Number(report.giftCardTotal || 0)
        const kulturnaIskaznicaCount = Number(report.kulturnaIskaznicaCount || 0)
        const kulturnaIskaznicaTotal = Number(report.kulturnaIskaznicaTotal || 0)
        const kulturnaIskaznicaTicketSellerCount = Number(
          report.kulturnaIskaznicaTicketSellerCount || 0,
        )
        const kulturnaIskaznicaTicketSellerTotal = Number(
          report.kulturnaIskaznicaTicketSellerTotal || 0,
        )
        const kulturnaIskaznicaCoreEventSalesPointCount = Number(
          report.kulturnaIskaznicaCoreEventSalesPointCount || 0,
        )
        const kulturnaIskaznicaCoreEventSalesPointTotal = Number(
          report.kulturnaIskaznicaCoreEventSalesPointTotal || 0,
        )
        const kulturnaIskaznicaFullOnlineCount = Number(
          report.kulturnaIskaznicaFullOnlineCount || 0,
        )
        const kulturnaIskaznicaFullOnlineTotal = Number(
          report.kulturnaIskaznicaFullOnlineTotal || 0,
        )
        const kulturnaIskaznicaCashCount = Number(report.kulturnaIskaznicaCashCount || 0)
        const kulturnaIskaznicaCashTotal = Number(report.kulturnaIskaznicaCashTotal || 0)
        const kulturnaIskaznicaCashKiAmount = Number(report.kulturnaIskaznicaCashKiAmount || 0)
        const kulturnaIskaznicaCashRemainderAmount = Number(
          report.kulturnaIskaznicaCashRemainderAmount || 0,
        )
        const kulturnaIskaznicaCardCount = Number(report.kulturnaIskaznicaCardCount || 0)
        const kulturnaIskaznicaCardTotal = Number(report.kulturnaIskaznicaCardTotal || 0)
        const kulturnaIskaznicaCardKiAmount = Number(report.kulturnaIskaznicaCardKiAmount || 0)
        const kulturnaIskaznicaCardRemainderAmount = Number(
          report.kulturnaIskaznicaCardRemainderAmount || 0,
        )
        const kulturnaIskaznicaOnlineCount = Number(report.kulturnaIskaznicaOnlineCount || 0)
        const kulturnaIskaznicaOnlineTotal = Number(report.kulturnaIskaznicaOnlineTotal || 0)
        const kulturnaIskaznicaOnlineKiAmount = Number(report.kulturnaIskaznicaOnlineKiAmount || 0)
        const kulturnaIskaznicaOnlineRemainderAmount = Number(
          report.kulturnaIskaznicaOnlineRemainderAmount || 0,
        )

        this.totalSoldAmount += totalAmount
        this.totalSoldCount += onlineCount + offlineCount + giftCardCount

        if (!report.isOrderForm) {
          this.totalOnlineSoldAmount += onlineTotal
          this.totalOnlineSoldCount += onlineCount

          this.totalTicketSellerSoldAmount += ticketSellerTotal
          this.totalTicketSellerSoldCount += ticketSellerCount

          this.totalCoreEventSalesPointSoldAmount += coreEventTotal
          this.totalCoreEventSalesPointSoldCount += coreEventCount

          this.totalGiftCardSoldCount += giftCardCount
          this.totalGiftCardSoldAmount += giftCardTotal

          this.totalKulturnaIskaznicaCount += kulturnaIskaznicaCount
          this.totalKulturnaIskaznicaAmount += kulturnaIskaznicaTotal
          this.totalKulturnaIskaznicaTicketSellerCount += kulturnaIskaznicaTicketSellerCount
          this.totalKulturnaIskaznicaTicketSellerAmount += kulturnaIskaznicaTicketSellerTotal
          this.totalKulturnaIskaznicaCoreEventSalesPointCount +=
            kulturnaIskaznicaCoreEventSalesPointCount
          this.totalKulturnaIskaznicaCoreEventSalesPointAmount +=
            kulturnaIskaznicaCoreEventSalesPointTotal
          this.totalKulturnaIskaznicaFullOnlineCount += kulturnaIskaznicaFullOnlineCount
          this.totalKulturnaIskaznicaFullOnlineAmount += kulturnaIskaznicaFullOnlineTotal
          this.totalKulturnaIskaznicaCashCount += kulturnaIskaznicaCashCount
          this.totalKulturnaIskaznicaCashAmount += kulturnaIskaznicaCashTotal
          this.totalKulturnaIskaznicaCashKiAmount += kulturnaIskaznicaCashKiAmount
          this.totalKulturnaIskaznicaCashRemainderAmount += kulturnaIskaznicaCashRemainderAmount
          this.totalKulturnaIskaznicaCardCount += kulturnaIskaznicaCardCount
          this.totalKulturnaIskaznicaCardAmount += kulturnaIskaznicaCardTotal
          this.totalKulturnaIskaznicaCardKiAmount += kulturnaIskaznicaCardKiAmount
          this.totalKulturnaIskaznicaCardRemainderAmount += kulturnaIskaznicaCardRemainderAmount
          this.totalKulturnaIskaznicaOnlineCount += kulturnaIskaznicaOnlineCount
          this.totalKulturnaIskaznicaOnlineAmount += kulturnaIskaznicaOnlineTotal
          this.totalKulturnaIskaznicaOnlineKiAmount += kulturnaIskaznicaOnlineKiAmount
          this.totalKulturnaIskaznicaOnlineRemainderAmount +=
            kulturnaIskaznicaOnlineRemainderAmount
        } else {
          this.totalOrderFormSoldAmount += totalAmount
        }
      }
      const ticketSellerAmount = this.totalTicketSellerSoldAmount
      const coreEventAmount = this.totalCoreEventSalesPointSoldAmount

      this.totalSoldAmount = this.round(this.totalSoldAmount)
      this.totalOnlineSoldAmount = this.round(this.totalOnlineSoldAmount)
      this.totalTicketSellerSoldAmount = this.round(ticketSellerAmount)
      this.totalCoreEventSalesPointSoldAmount = this.round(coreEventAmount)
      this.totalOrderFormSoldAmount = this.round(this.totalOrderFormSoldAmount)
      this.totalKulturnaIskaznicaAmount = this.round(this.totalKulturnaIskaznicaAmount)
      this.totalKulturnaIskaznicaTicketSellerAmount = this.round(
        this.totalKulturnaIskaznicaTicketSellerAmount,
      )
      this.totalKulturnaIskaznicaCoreEventSalesPointAmount = this.round(
        this.totalKulturnaIskaznicaCoreEventSalesPointAmount,
      )
      this.totalKulturnaIskaznicaFullOnlineAmount = this.round(
        this.totalKulturnaIskaznicaFullOnlineAmount,
      )
      this.totalKulturnaIskaznicaCashAmount = this.round(this.totalKulturnaIskaznicaCashAmount)
      this.totalKulturnaIskaznicaCashKiAmount = this.round(
        this.totalKulturnaIskaznicaCashKiAmount,
      )
      this.totalKulturnaIskaznicaCashRemainderAmount = this.round(
        this.totalKulturnaIskaznicaCashRemainderAmount,
      )
      this.totalKulturnaIskaznicaCardAmount = this.round(this.totalKulturnaIskaznicaCardAmount)
      this.totalKulturnaIskaznicaCardKiAmount = this.round(this.totalKulturnaIskaznicaCardKiAmount)
      this.totalKulturnaIskaznicaCardRemainderAmount = this.round(
        this.totalKulturnaIskaznicaCardRemainderAmount,
      )
      this.totalKulturnaIskaznicaOnlineAmount = this.round(this.totalKulturnaIskaznicaOnlineAmount)
      this.totalKulturnaIskaznicaOnlineKiAmount = this.round(
        this.totalKulturnaIskaznicaOnlineKiAmount,
      )
      this.totalKulturnaIskaznicaOnlineRemainderAmount = this.round(
        this.totalKulturnaIskaznicaOnlineRemainderAmount,
      )
      this.totalOfflineSoldAmount = this.round(ticketSellerAmount + coreEventAmount)
      this.totalOfflineSoldCount =
        this.totalTicketSellerSoldCount + this.totalCoreEventSalesPointSoldCount

      this.offlineRecapitulationCash = { count: 0, total: 0 }
      this.offlineRecapitulationCashAll = { count: 0, total: 0 }
      this.offlineRecapitulationCardsAll = { count: 0, total: 0 }
      this.offlineRecapitulationKulturnaIskaznica = { count: 0, total: 0 }
      this.offlineRecapitulationKulturnaIskaznicaCash = { count: 0, total: 0 }
      this.offlineRecapitulationKulturnaIskaznicaCard = { count: 0, total: 0 }
      this.offlineRecapitulationCards = []

      const offlineByPaymentType = (eventReport.offlineSoldTicketsByPaymentMethod || []).filter(
        item => !item.isCoreEventSalesPoint,
      )

      offlineByPaymentType.forEach(item => {
        const count = Number(item.count || 0)
        const total = Number(item.total || 0)

        if (item.paymentType === 'CASH') {
          this.offlineRecapitulationCash = {
            count,
            total: this.round(total),
          }
          this.offlineRecapitulationCashAll.count += count
          this.offlineRecapitulationCashAll.total = this.round(
            this.offlineRecapitulationCashAll.total + total,
          )
        }

        if (item.paymentType === 'CARD') {
          this.offlineRecapitulationCards.push({
            bankCardType: item.bankCard || 'Kartica',
            count,
            total: this.round(total),
          })
          this.offlineRecapitulationCardsAll.count += count
          this.offlineRecapitulationCardsAll.total = this.round(
            this.offlineRecapitulationCardsAll.total + total,
          )
        }

        if (item.paymentType === 'KULTURNA_ISKAZNICA') {
          this.offlineRecapitulationKulturnaIskaznica = {
            count,
            total: this.round(total),
          }
        }

        if (item.paymentType === 'KULTURNA_ISKAZNICA_CASH') {
          this.offlineRecapitulationKulturnaIskaznicaCash = {
            count,
            total: this.round(total),
          }
          this.offlineRecapitulationCashAll.count += count
          this.offlineRecapitulationCashAll.total = this.round(
            this.offlineRecapitulationCashAll.total + total,
          )
        }

        if (item.paymentType === 'KULTURNA_ISKAZNICA_CARD') {
          this.offlineRecapitulationKulturnaIskaznicaCard = {
            count,
            total: this.round(total),
          }
          this.offlineRecapitulationCardsAll.count += count
          this.offlineRecapitulationCardsAll.total = this.round(
            this.offlineRecapitulationCardsAll.total + total,
          )
        }
      })
    },
    getSaleChannelLabel(report) {
      const onlineCount = Number(report.online || 0)
      const offlineCount = Number(report.offline || 0)
      const isCoreEventSalesPoint = Boolean(report.isCoreEventSalesPoint)
      const offlineLabel = isCoreEventSalesPoint
        ? `FIZIČKE - ${report.coreEventSalesPointName || 'CoreEvent prodajno mjesto'}`
        : 'FIZIČKE - blagajna'
      const offlineWithCount = offlineCount ? `${offlineLabel} (${offlineCount})` : offlineLabel
      if (offlineCount && !onlineCount) {
        return offlineWithCount
      }
      if (onlineCount && !offlineCount) {
        return `ONLINE (${onlineCount})`
      }
      if (onlineCount && offlineCount) {
        return `ONLINE (${onlineCount}) / ${offlineWithCount}`
      }

      return '-'
    },
    calculateShare(count) {
      const denominator = this.totalGratisCount + this.totalSoldCount
      if (!denominator) {
        return 0
      }

      return this.round((100 * count) / denominator)
    },
  },
  apollo: {
    getOrganization: {
      query: GET_ORGANIZATION,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
        }
      },
      manual: true,
      result({ data, loading }) {
        if (loading || !data.getOrganization) {
          return
        }
        this.organization = data.getOrganization
      },
    },
  },
}
</script>

<style scoped>
.main-button {
  width: 100px;
}

@media print {
  .main-button {
    display: none !important;
  }
  .no-print {
    display: none !important;
  }
}

.page-container {
  width: 210mm;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.table-info {
  width: 50%;
}

canvas {
  height: 230px !important;
}

.divider {
  height: 1px;
  width: 100%;
  background: #0277bd;
}

.table-info section {
  display: flex;
  justify-content: space-between;
}

.table-info section p {
  font-size: 12px;
}

.page-title {
  color: #0277bd;
  text-align: center;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.72px;
}

.organization-data {
  color: #0277bd;
  font-family: Source Sans Pro;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%;
}

.basic {
  color: #0277bd;
  font-family: Source Sans Pro;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.header-data {
  color: #333;
  font-family: Source Sans Pro;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  padding: 30px 10px 10px;
  line-height: 120%;
  flex: 1;
}

.report-data {
  color: #333;
  font-family: Source Sans Pro;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  padding: 10px;
  line-height: 120%;
  flex: 1;
}

.report-row {
  display: flex;
}

.date {
  color: #0277bd;
  text-align: right;
  font-family: Source Sans Pro;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.report-title {
  border-radius: 4px 4px 0px 0px;
  background: #f5f5f5;
  display: flex;
}

.report-title p {
  flex: 1;
}

.text-right {
  text-align: right;
}
.order-with-description {
  display: flex;
  align-items: center;
  border-top: 1px solid #f5f5f5;
  border-radius: 0;
}
</style>
